import { useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { alertService } from '../../../../shared/services/alertService';
import { usePlanningWizard } from '../PlanningWizardContext';
import { SeniorityEntry } from '../types';

/**
 * Step 9 — Seniority Order (Angular addOneMoreSeniority / removeSeniorityList /
 * senioritySubmit). Ordered add/remove list; the trailing blank row renders the
 * add controls. Selected orders are removed from the available list.
 */
const StepSeniority = () => {
  const {
    orderList,
    setOrderList,
    allOrderList,
    addedSeniorityList,
    setAddedSeniorityList,
    nextPage,
    previousPage,
  } = usePlanningWizard();

  const [order, setOrder] = useState('');

  const addOneMoreSeniority = (ind: number) => {
    if (!order) {
      alertService.error('Please select a condition');
      return;
    }
    const fil = orderList.filter((ft) => ft.value === order);
    if (fil.length > 0) {
      setAddedSeniorityList((prev) => {
        const next = prev.map((row, i) =>
          i === ind
            ? {
                ...row,
                label: fil[0].label,
                value: fil[0].value,
                OrderKey: fil[0].OrderKey,
              }
            : row
        );
        return [...next, { label: '', value: '', OrderKey: '' }];
      });
    }
    setOrderList((prev) => prev.filter((obj) => obj.value !== order));
    setOrder('');
  };

  const removeSeniorityList = (ind: number, obj: SeniorityEntry) => {
    setAddedSeniorityList((prev) => prev.filter((_, i) => i !== ind));
    const add = allOrderList.filter((ft) => ft.value === obj.value);
    if (add.length > 0) setOrderList((prev) => [...prev, add[0]]);
  };

  const senioritySubmit = () => {
    if (addedSeniorityList.length > 0 && addedSeniorityList[0].label) {
      nextPage();
    } else {
      alertService.error('Please order seniority');
    }
  };

  return (
    <div className="card p-4">
      <DataTable
        key={`seniority-${order}-${orderList.length}`}
        value={addedSeniorityList}
        responsiveLayout="scroll"
      >
        <Column
          header="Order"
          style={{ width: '50%' }}
          body={(row: SeniorityEntry) =>
            row.label ? (
              <span>{row.label}</span>
            ) : (
              <Dropdown
                value={order}
                options={orderList}
                optionLabel="label"
                optionValue="value"
                placeholder="Select.."
                showClear={!!order}
                appendTo={document.body}
                className="w-100 dropdown-fixed"
                panelClassName="dropdown-panel-fit"
                onChange={(e) => setOrder(e.value ?? '')}
              />
            )
          }
        />
        <Column
          header="Action"
          style={{ width: '50%' }}
          body={(row: SeniorityEntry, options) =>
            row.label ? (
              <span>{options.rowIndex + 1}</span>
            ) : (
              <Button
                type="button"
                icon="pi pi-plus"
                label="Add"
                className="p-button-success"
                onClick={() => addOneMoreSeniority(options.rowIndex)}
              />
            )
          }
        />
        <Column
          header="Remove"
          alignHeader="center"
          body={(row: SeniorityEntry, options) =>
            row.label ? (
              <div className="text-center">
                <i
                  className="pi pi-times-circle text-danger cursor-pointer fs-5"
                  role="button"
                  tabIndex={0}
                  onClick={() => removeSeniorityList(options.rowIndex, row)}
                />
              </div>
            ) : null
          }
        />
      </DataTable>

      <div className="row mt-4">
        <div className="col-6">
          <Button
            type="button"
            icon="pi pi-angle-left"
            label="Previous"
            className="p-button-danger"
            onClick={previousPage}
          />
        </div>
        <div className="col-6 text-end">
          <Button
            type="button"
            icon="pi pi-angle-right"
            iconPos="right"
            label="Next"
            className="p-button-success"
            onClick={senioritySubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default StepSeniority;
